import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Popup from '@components/popups/basic-modal'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { phoneNumberAutomasking } from '@helpers/utility'
import { numberMask } from '@helpers/inputmask'
import * as Yup from 'yup'
import { segmentEventTrack } from '@helpers/segment'
import config from '@config/index'
import notification from '@app/components/notification'
import { useCaptchaVerification } from '@app/helpers/useCaptchaVerification'
import { useCodeTimer } from '@app/helpers/useCodeTimer'
import { OTPVerificationView, phoneEntryStyles } from '@components/otp/OTPVerificationView'
import { useTranslation } from 'react-i18next'

export const VerifyMobilePopup = ({
  open,
  onClose,
  apiError,
  verifyOtp,
  onRequestOtp,
  onRequestOtpOnCall,
  securityText,
  title
}) => {
  const { t } = useTranslation()
  const [submitting, setSubmitting] = useState(false)
  const [otpPage, setOtpPage] = useState(false)
  const [otpSubmitting, setOtpSubmitting] = useState(false)
  const [contactPhone, setContactPhone] = useState('')
  const [digits, setDigits] = useState(['', '', '', ''])
  const digitRefs = [useRef(), useRef(), useRef(), useRef()]
  const codeTimer = useCodeTimer(600)

  const dispatch = useDispatch()
  const me = useSelector((state) => state.auth.me)
  const {
    capthcaValue,
    noOfRetries,
    isResendCodeDisabled,
    showReCaptcha,
    timer,
    onChangeRecaptche,
    setIsResendCodeDisabled,
    resetTimer,
    incrementNumberOfRetries
  } = useCaptchaVerification()

  useEffect(() => {
    const getState = async () => {
      if (!me) await dispatch.auth.getMe()
    }
    getState()
  }, [])

  const allDigitsFilled = digits.every((d) => d !== '')

  const focusDigit = (index) => {
    if (digitRefs[index] && digitRefs[index].current) {
      digitRefs[index].current.focus()
    }
  }

  const handleDigitChange = (index, value) => {
    const val = value.replace(/\D/g, '').slice(-1)
    const next = [...digits]
    next[index] = val
    setDigits(next)
    if (val && index < 3) {
      focusDigit(index + 1)
    }
  }

  const handleDigitKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      const next = [...digits]
      next[index - 1] = ''
      setDigits(next)
      focusDigit(index - 1)
    }
  }

  const handleDigitPaste = (e) => {
    e.preventDefault()
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 4)
    const next = ['', '', '', '']
    pasted.split('').forEach((ch, i) => {
      next[i] = ch
    })
    setDigits(next)
    focusDigit(Math.min(pasted.length, 3))
  }

  const trackPhoneNumber = (valid) => {
    segmentEventTrack({
      event: 'entered_mobile_phone_verification',
      properties: {
        user_id: me && me._id,
        valid_number: valid,
        role: me && me.role_id && me.role_id.role_name
      }
    })
  }

  const trackResendOTP = (type) => {
    segmentEventTrack({
      event: 'retried_mobile_phone_verification',
      properties: {
        user_id: me && me._id,
        retry_method: type,
        role: me && me.role_id && me.role_id.role_name
      }
    })
  }

  const trackOTP = (valid) => {
    segmentEventTrack({
      event: 'entered_code_verification',
      properties: {
        user_id: me && me._id,
        valid_code: valid,
        role: me && me.role_id && me.role_id.role_name
      }
    })
  }

  const handlePhoneSubmit = async (values) => {
    setSubmitting(true)
    const resData = await onRequestOtp({ contact_phone: values.txt_phone })
    if (!resData) {
      trackPhoneNumber(false)
    } else {
      resetTimer()
      trackPhoneNumber(true)
      setContactPhone(values.txt_phone)
      setOtpPage(true)
      setDigits(['', '', '', ''])
      codeTimer.start(600)
      setTimeout(() => focusDigit(0), 100)
    }
    setSubmitting(false)
  }

  const handleResend = async () => {
    incrementNumberOfRetries()
    setIsResendCodeDisabled(true)
    resetTimer()
    setDigits(['', '', '', ''])
    codeTimer.start(600)
    setTimeout(() => focusDigit(0), 100)
    trackResendOTP('text')
    await onRequestOtp({ contact_phone: contactPhone })
  }

  const handleOtpOnCall = async () => {
    incrementNumberOfRetries()
    setIsResendCodeDisabled(true)
    resetTimer()
    setDigits(['', '', '', ''])
    codeTimer.start(600)
    setTimeout(() => focusDigit(0), 100)
    trackResendOTP('call')
    await onRequestOtpOnCall({ contact_phone: contactPhone })
  }

  const handleOtpSubmit = async () => {
    if (noOfRetries >= 2 && !capthcaValue) {
      notification('error', t('common.please-complete-the-recaptcha') || 'Please complete the reCaptcha')
      return
    }
    if (!allDigitsFilled) return
    setOtpSubmitting(true)
    const payload = {
      ...me,
      otp: digits.join(''),
      contact_phone: contactPhone
    }
    const noopResetForm = () => {}
    const res = await verifyOtp({ payload, resetForm: noopResetForm })
    if (res) {
      trackOTP(true)
      handleClose()
    } else {
      trackOTP(false)
    }
    setOtpSubmitting(false)
  }

  const handleClose = () => {
    setOtpPage(false)
    setSubmitting(false)
    setOtpSubmitting(false)
    setContactPhone('')
    setDigits(['', '', '', ''])
    codeTimer.stop()
    onClose()
  }

  const resendDisabled = isResendCodeDisabled && timer !== 0

  return (
    <Popup
      id="modalPhoneVerify7"
      open={open}
      title=""
      onCancel={handleClose}
      type="small"
      modalBackdropClicked={handleClose}
      hideHeader={true}>
      {!otpPage ? (
        <React.Fragment>
          <div style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ padding: '40px 32px 0' }}>
              <div style={phoneEntryStyles.iconWrap}>
                <div style={phoneEntryStyles.iconDashedRing} />
                <img
                  alt=""
                  src="https://dufzo4epsnvlh.cloudfront.net/image/icon-new/verify-icon.svg"
                  style={{ width: 28, height: 28 }}
                />
              </div>
              <h2 style={phoneEntryStyles.title}>
                {title || t('common.verify-your-account') || 'Verify your account'}
              </h2>
              {securityText && <p style={{ ...phoneEntryStyles.subtitle, marginBottom: 8 }}>{securityText}</p>}
              <p style={phoneEntryStyles.subtitle}>
                {t(
                  'common.enter-your-mobile-number-below-and-we-will-send-you-a-confirmation-code-in-the-next-few-seconds'
                )}
              </p>
            </div>
          </div>

          <Formik
            validateOnBlur={false}
            validateOnChange={false}
            enableReinitialize
            initialValues={{
              txt_phone: contactPhone ? phoneNumberAutomasking(contactPhone.replace('+1', '')) : ''
            }}
            validationSchema={Yup.object().shape({
              txt_phone: Yup.string().required(t('common.required'))
            })}
            onSubmit={handlePhoneSubmit}>
            {({ values, setFieldValue }) => (
              <Form id="frm_phoneVerify">
                <button type="button" className="close remove-modal-close" onClick={handleClose}>
                  <span aria-hidden="true">&times;</span>
                </button>
                <div style={{ padding: '0 32px' }}>
                  <ErrorMessage
                    name="txt_phone"
                    component="div"
                    className="form-error"
                    style={{ textAlign: 'center', marginBottom: 8 }}
                  />
                  <Field
                    className="form-control form-control-lg"
                    type="text"
                    placeholder={t('common.phone-number') || 'Phone Number'}
                    name="txt_phone"
                    value={values.txt_phone ? numberMask(values.txt_phone.replace('+1', '')) : ''}
                    onChange={(event) => {
                      setFieldValue('txt_phone', numberMask(event.target.value))
                    }}
                    id="frm_phoneVerifyPhone_txtPhone"
                    maxLength={14}
                    style={{ height: 48, textAlign: 'center', fontSize: 16, letterSpacing: 1 }}
                  />
                </div>
                <footer style={{ padding: '16px 32px' }}>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="otp-verify-btn"
                    style={{
                      ...phoneEntryStyles.verifyBtn,
                      fontFamily: '"Open Sans Bold", sans-serif',
                      fontWeight: 700,
                      ...(submitting ? phoneEntryStyles.verifyBtnDisabled : {})
                    }}
                    id="frm_phoneVerifyPhone_btnNext">
                    {t('common.next') || 'Next'}
                    {submitting && (
                      <img
                        className="spinner"
                        src="https://dufzo4epsnvlh.cloudfront.net/image/default.svg"
                        alt=""
                        style={{ width: 18, height: 18, marginLeft: 6 }}
                      />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    style={{
                      width: '100%',
                      background: '#f1f5f9',
                      border: 'none',
                      borderRadius: 4,
                      color: '#64748b',
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                      marginTop: 12,
                      padding: '12px 0'
                    }}>
                    {t('common.skip') || 'Skip'}
                  </button>
                </footer>
              </Form>
            )}
          </Formik>
        </React.Fragment>
      ) : (
        <OTPVerificationView
          phoneNumber={contactPhone}
          digits={digits}
          onDigitChange={handleDigitChange}
          onDigitKeyDown={handleDigitKeyDown}
          onDigitPaste={handleDigitPaste}
          digitRefs={digitRefs}
          titleText={t('common.enter-verification-code') || 'Verify your phone'}
          subtitleText={t('common.a-4-digit-code-has-been-sent-to') || 'We sent a 4-digit code to'}
          timerFormatted={codeTimer.formatted}
          timerExpired={codeTimer.expired}
          expiredText={t('common.code-expired') || 'Code expired — request a new one below'}
          timerLabel={t('common.code-expires-in') || 'Code expires in'}
          onVerify={handleOtpSubmit}
          verifying={otpSubmitting}
          verifyDisabled={!allDigitsFilled || otpSubmitting || codeTimer.expired || (showReCaptcha && !capthcaValue)}
          verifyText={t('common.verify') || 'Verify Code'}
          verifyingText={t('common.verifying') || 'Verifying...'}
          showSmsResend={true}
          showCallResend={true}
          onResendSms={handleResend}
          onResendCall={handleOtpOnCall}
          resendDisabled={resendDisabled}
          resendTimerText={resendDisabled && !codeTimer.expired ? `(${timer}s)` : null}
          smsLabel={t('common.resend-sms') || 'Resend SMS'}
          callLabel={t('common.call-me') || 'Call me'}
          showReCaptcha={showReCaptcha}
          reCaptchaSiteKey={config.googleReCAPTCHAKey}
          onCaptchaChange={onChangeRecaptche}
          showRetryOptions={noOfRetries <= 3}
          onClose={handleClose}
        />
      )}
    </Popup>
  )
}
